import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Book, MessageSquare, Users, FileText, Download, Settings, Shield } from 'lucide-react';

const Guide = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: 'Overview', icon: Book },
    { id: 'chat', title: 'Using Chat AI', icon: MessageSquare },
    { id: 'roles', title: 'User Roles', icon: Users },
    { id: 'export', title: 'Data Export', icon: Download },
    { id: 'features', title: 'Key Features', icon: FileText },
    { id: 'admin', title: 'Admin Features', icon: Shield },
  ];

  const content = {
    overview: {
      title: 'Platform Overview',
      sections: [
        {
          heading: 'What is the Site Intelligence Platform?',
          content: `The Site Intelligence Platform is an AI-powered tool designed to help sales teams and account managers efficiently manage and analyze website data. It combines real-time analytics with intelligent AI assistance to streamline your workflow.`
        },
        {
          heading: 'Key Capabilities',
          content: `
• **AI-Powered Chat**: Ask questions in natural language and get instant insights
• **Site Analytics**: View revenue data and performance metrics for all sites
• **Smart Data Storage**: The AI remembers information you provide and makes it available to your team
• **Export Functionality**: Download site data as CSV for external analysis
• **Role-Based Access**: Different features for salespeople, account managers, and admins
          `
        },
        {
          heading: 'Getting Started',
          content: `
1. Log in with your credentials
2. You'll be directed to the Dashboard where you can see analytics and chat
3. Use the Chat AI to ask questions or provide information about sites
4. Access the export panel to download data
5. Admins can access the Admin Dashboard for user management
          `
        }
      ]
    },
    chat: {
      title: 'Using the Chat AI',
      sections: [
        {
          heading: 'How to Chat',
          content: `Simply type your questions or statements in natural language. The AI understands context and remembers your conversation history.`
        },
        {
          heading: 'What You Can Ask',
          content: `
**For Salespeople:**
• "What sites are available in the tech category?"
• "Show me premium sites with high traffic"
• "What do we know about reddit.com?"
• "Find sites for a healthcare client"

**For Account Managers:**
• "I am the AM for stackoverflow and github"
• "stackoverflow belongs to the tech category"
• "github pricing is $20 CPM"
• "reddit has 100M monthly visitors"

**General Questions:**
• "What sites generated the most revenue yesterday?"
• "Show me all sports category sites"
• "Export all sites with pricing information"
          `
        },
        {
          heading: 'Providing Information',
          content: `The AI automatically saves information you provide about sites. Just state it naturally:

• "I manage these sites: reddit, twitter, facebook"
• "quizlet should be classified as education"
• "dailymotion pricing is $15 CPM"
• "stackoverflow has 50M monthly visitors"

The AI will extract this data and make it available to your team automatically.`
        },
        {
          heading: 'AI Memory',
          content: `The AI remembers:
• Sites you manage (for AMs)
• Information you've provided about sites
• Your conversation history
• Your role and department

This means you don't need to repeat yourself, and the AI becomes more helpful over time.`
        }
      ]
    },
    roles: {
      title: 'User Roles & Permissions',
      sections: [
        {
          heading: 'Salesperson',
          content: `**Purpose**: Find and analyze sites for client proposals

**What You Can Do:**
• Search for sites by category, traffic, or characteristics
• View AI analysis and saved data about sites
• Export site lists for proposals
• Ask the AI for recommendations based on client needs
• View analytics data

**Daily Usage Cap**: $2.00 worth of AI requests

**Best Practices:**
• Ask specific questions about client requirements
• Request site recommendations based on industry/audience
• Use the export feature to create client-ready spreadsheets
          `
        },
        {
          heading: 'Account Manager',
          content: `**Purpose**: Manage site relationships and provide verified data

**What You Can Do:**
• Everything a Salesperson can do, plus:
• Declare which sites you manage
• Provide and update site information (category, pricing, traffic, etc.)
• Verify or correct AI analysis
• Add notes and details about your sites

**Daily Usage Cap**: $5.00 worth of AI requests

**Best Practices:**
• Tell the AI which sites you manage: "I am the AM for site1, site2, site3"
• Provide detailed information: category, pricing, traffic, demographics
• Correct AI analysis when needed
• Keep site information up to date
          `
        },
        {
          heading: 'Admin',
          content: `**Purpose**: Platform management and oversight

**What You Can Do:**
• Everything other roles can do, plus:
• Access Admin Dashboard
• View all users and their activity
• Reset user passwords
• Delete user accounts
• Monitor platform usage and costs
• View system analytics

**Daily Usage Cap**: $10.00 worth of AI requests

**Admin Dashboard Features:**
• User management (view, reset password, delete)
• Usage statistics
• Cost tracking
• System health monitoring
          `
        }
      ]
    },
    export: {
      title: 'Data Export',
      sections: [
        {
          heading: 'CSV Export',
          content: `Export site data to CSV format for use in spreadsheets, proposals, or external tools.`
        },
        {
          heading: 'How to Export',
          content: `
**Method 1: Export Panel**
1. Open the export panel in the sidebar
2. Choose your filters (category, AM, pricing availability)
3. Click "Download CSV"

**Method 2: Ask the AI**
• "Export all tech sites"
• "Download sites with pricing information"
• "Give me a CSV of premium sites"

The AI will tell you how to access the export feature.
          `
        },
        {
          heading: 'Export Filters',
          content: `
• **Category**: Filter by site category (tech, sports, entertainment, etc.)
• **Account Manager**: Filter by AM owner
• **Pricing Available**: Show only sites with pricing information
• **Premium Only**: Export only premium-tier sites
          `
        },
        {
          heading: 'CSV Format',
          content: `Exported files include:
• Site URL
• Internal category
• Account Manager
• Revenue (yesterday)
• Pricing information
• Traffic data
• Premium status
• Additional notes

Perfect for importing into Google Sheets, Excel, or CRM systems.`
        }
      ]
    },
    features: {
      title: 'Key Features',
      sections: [
        {
          heading: 'Real-Time Analytics',
          content: `View yesterday's revenue data for all sites. The dashboard updates daily with:
• Total revenue across all sites
• Revenue per site
• Top performing sites
• Category breakdowns`
        },
        {
          heading: 'Intelligent Data Storage',
          content: `The platform automatically saves information in two ways:

**Site Data**: Information about specific websites (category, pricing, traffic, AM owner, etc.)
**User Data**: Your role-specific information (sites you manage, preferences, etc.)

This data is:
• Automatically extracted from your chat conversations
• Shared with your team (where appropriate)
• Version tracked with history
• Always available when discussing those sites`
        },
        {
          heading: 'Semantic Search',
          content: `The AI uses advanced semantic search to find relevant sites even when you don't know exact names:

• "Find sites for a healthcare startup targeting millennials"
• "Show me high-traffic entertainment sites"
• "What premium tech sites do we have?"

The AI understands intent and context, not just keywords.`
        },
        {
          heading: 'Cost Tracking',
          content: `The platform tracks AI usage costs to ensure responsible use:

• Each user has a daily spending cap based on their role
• You can see your usage in real-time
• Costs reset daily at midnight UTC
• Admins can view all usage statistics

This ensures the AI remains available and cost-effective for everyone.`
        },
        {
          heading: 'Conversation History',
          content: `Your conversations with the AI are saved and can be continued later:

• Each conversation has a unique ID
• History is preserved across sessions
• The AI remembers context from earlier in the conversation
• You can reference previous discussions`
        }
      ]
    },
    admin: {
      title: 'Admin Features',
      sections: [
        {
          heading: 'Admin Dashboard Access',
          content: `Navigate to /admin or click "Admin Dashboard" in the navigation menu (visible only to admins).`
        },
        {
          heading: 'User Management',
          content: `
**View All Users:**
See a complete list of platform users with their:
• Name and email
• Role and department
• Account creation date
• Last login time
• Usage statistics

**Reset Passwords:**
Generate a new temporary password for any user. The new password will be displayed once.

**Delete Users:**
Permanently remove user accounts. This action cannot be undone.
          `
        },
        {
          heading: 'Usage Monitoring',
          content: `
Track platform usage across all users:
• Total daily costs
• Per-user spending
• Request counts
• Token usage
• Cap violations

This helps identify:
• Heavy users who might need higher caps
• Unusual usage patterns
• Cost optimization opportunities
          `
        },
        {
          heading: 'System Analytics',
          content: `
View platform-wide metrics:
• Total sites in the system
• Sites with complete data
• Category distribution
• AM assignments
• Revenue trends
          `
        },
        {
          heading: 'Best Practices for Admins',
          content: `
• Regularly review user activity
• Monitor costs to stay within budget
• Ensure AMs are providing site data
• Help users who hit their daily caps
• Keep user information up to date
• Review and adjust caps as needed
          `
        }
      ]
    }
  };

  const renderContent = (section) => {
    return content[section].sections.map((item, idx) => (
      <div key={idx} className="mb-8">
        <h3 className="text-xl font-semibold mb-3 text-gray-800">{item.heading}</h3>
        <div className="text-gray-700 whitespace-pre-line leading-relaxed">
          {item.content}
        </div>
      </div>
    ));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Dashboard
              </button>
              <h1 className="text-2xl font-bold text-gray-900">Platform Guide</h1>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <MessageSquare className="w-4 h-4" />
              <span>Ask the Chat AI if you have questions!</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-8">
              <h2 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                Contents
              </h2>
              <nav className="space-y-1">
                {sections.map((section) => {
                  const Icon = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{section.title}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm p-8">
              <h2 className="text-3xl font-bold mb-6 text-gray-900">
                {content[activeSection].title}
              </h2>
              {renderContent(activeSection)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Guide;
